import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { useTenantTranslation } from "hooks/formatters";

import {
  ChildMixUpdate,
  MaterialRead,
  ProductionMixResultRead,
  SteelGrade,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";

import equal from "fast-deep-equal";
import { useTenant } from "hooks/settings";
import { useMixQuantity } from "hooks/useMixQuantity.tsx";
import React from "react";
import {
  planMixTableEditsSlice,
  selectPlanMixTableEdits,
} from "src/store/slices/planMixesTableEditSlice";
import { useAppDispatch, useAppSelector, useAppStore } from "src/store/store";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { SteelGradesSelect } from "./SteelGradesSelect";
import { Row } from "./types";
import {
  isMixEqual,
  makeRows,
  mergeRowsAndMix,
  tableRowsToBackendMixes,
} from "./utils";
import { PlanId } from "../boundary/PlanId";
import { Period } from "hooks/periodIndex";
import { DriMonthToDateConsumption } from "./DriMonthToDateConsumption";
import { PlanAncestorsTooltip } from "../planHistory/PlanAncestorsTooltip";
import { CommentCell } from "./CommentCell";

interface Props {
  timeString: string;
  view: "backup" | "supervisor" | "deploy";
  planId: PlanId;
  period: Period;
  materials: MaterialRead[];
  steelGrades: SteelGrade[];
  results: ProductionMixResultRead[];
  showDriConsumption: boolean;
  onSubmit: (mixUpdates: ChildMixUpdate[], comment: string) => Promise<void>;
  children: (
    onChange: (planId: PlanId, period: Period, editedRows: Row[]) => void,
    setMixDescription: (mixId: number, description: string) => void,
    isSubmitting: boolean,
    hasEdited: boolean,
    rows: Row[]
  ) => React.JSX.Element;
}

type BodyProps = Props & {
  isSubmitting: boolean;
  originalRows: Row[];
  editedRows: Row[];
};

export const createPlanMixUpdates = (
  originalResults: ProductionMixResultRead[],
  newResults: ProductionMixResultRead[]
): ChildMixUpdate[] => {
  const originalById = typeSafeObjectFromEntries(
    originalResults.map((result) => [result.mix.id, result])
  );
  const editedResults = newResults.filter((result) => {
    const original = originalById[result.mix.id];
    return original === undefined || !isMixEqual(result, original);
  });
  return editedResults.map((result) => ({
    parent_mix_ids: [result.mix.id],
    new_mix_production: {
      name: result.name,
      description: result.description ?? null,
      production: result.production,
      mix: {
        baskets: result.mix.baskets,
        furnace: result.mix.furnace,
        thresholds: result.mix.thresholds,
      },
    },
  }));
};

export const EditableTableActions = ({
  timeString,
  view,
  planId,
  period,
  materials,
  steelGrades,
  results,
  showDriConsumption,
  onSubmit,
  children,
}: Props) => {
  const dispatch = useAppDispatch();
  const planEdits = useAppSelector((state) =>
    selectPlanMixTableEdits(state, planId, period)
  );
  const mixQuantity = useMixQuantity();

  React.useEffect(() => {
    if (!planEdits) {
      dispatch(
        planMixTableEditsSlice.actions.set([
          planId,
          period,
          makeRows(planId, period, results, steelGrades, mixQuantity),
        ])
      );
    }
  }, [planEdits, planId, period, results, steelGrades, mixQuantity, dispatch]);

  if (!planEdits) {
    return null;
  } else {
    const { isSubmitting, originalRows, editedRows } = planEdits;
    return (
      <Body
        timeString={timeString}
        view={view}
        planId={planId}
        period={period}
        materials={materials}
        steelGrades={steelGrades}
        results={results}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        originalRows={originalRows}
        editedRows={editedRows}
        showDriConsumption={showDriConsumption}
      >
        {children}
      </Body>
    );
  }
};

const Body = ({
  timeString,
  view,
  planId,
  period,
  materials,
  steelGrades,
  results,
  onSubmit,
  children,
  isSubmitting,
  originalRows,
  editedRows,
  showDriConsumption,
}: BodyProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const mixQuantity = useMixQuantity();
  const hasEdited = !equal(editedRows, originalRows);

  const handleOnTableChange = React.useCallback(
    (planId: PlanId, period: Period, editedRows: Row[]) => {
      dispatch(
        planMixTableEditsSlice.actions.update([planId, period, editedRows])
      );
    },
    [dispatch]
  );

  const handleOnCommentChange = React.useCallback(
    (comment: string) => {
      dispatch(
        planMixTableEditsSlice.actions.setComment([planId, period, comment])
      );
    },
    [dispatch, planId, period]
  );

  const setMixDescription = React.useCallback(
    (mixId: number, description: string) => {
      dispatch(
        planMixTableEditsSlice.actions.setMixDescription([
          planId,
          period,
          mixId,
          description,
        ])
      );
    },
    [dispatch, planId, period]
  );

  const [doSummarisePlanResultsMutation] = useSummarisePlanResultsMutation({
    fixedCacheKey: `${planId}-${period}`,
  });

  React.useEffect(() => {
    const mutation = doSummarisePlanResultsMutation({
      tenantName,
      unsummarisedPlanResults: {
        plan_id: planId,
        period,
        mixes: tableRowsToBackendMixes(editedRows),
      },
    });
    return () => {
      mutation.abort();
    };
  }, [doSummarisePlanResultsMutation, editedRows, tenantName, planId, period]);

  const handleOnCancel = React.useCallback(() => {
    dispatch(planMixTableEditsSlice.actions.clear([planId, period]));
  }, [dispatch, planId, period]);

  const handleOnSubmit = React.useCallback(() => {
    dispatch(planMixTableEditsSlice.actions.setIsSubmitting([planId, period]));
    setTimeout(() => {
      void (async () => {
        const planEdits = selectPlanMixTableEdits(
          store.getState(),
          planId,
          period
        );
        if (planEdits !== undefined) {
          const submitResults = results.map((result) =>
            mergeRowsAndMix(planEdits.editedRows, result)
          );
          const mixUpdates = createPlanMixUpdates(results, submitResults);
          await onSubmit(mixUpdates, planEdits.comment);
          dispatch(
            planMixTableEditsSlice.actions.setClearSubmitting([planId, period])
          );
          dispatch(planMixTableEditsSlice.actions.clear([planId, period]));
        }
      })();
    }, 500);
  }, [dispatch, planId, period, results, onSubmit, store]);

  return (
    <Stack sx={{ maxHeight: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={2}
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: "fontWeightBold",
                whiteSpace: "nowrap",
              }}
            >
              {timeString}
            </Typography>
            <Box>
              <Tooltip
                title={<PlanAncestorsTooltip planId={planId} />}
                placement="bottom"
                arrow
                enterDelay={100}
                leaveDelay={500}
                slotProps={{
                  popper: {
                    sx: {
                      "& .MuiTooltip-tooltip": {
                        bgcolor: "background.paper",
                        color: "text.primary",
                        p: 0,
                        maxWidth: "none",
                      },
                    },
                  },
                }}
              >
                <IconButton size="small">
                  <HistoryRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
          {mixQuantity == "mass" ? (
            <SteelGradesSelect
              view={view}
              planId={planId}
              period={period}
              steelGrades={steelGrades}
            />
          ) : null}
        </Stack>
        {showDriConsumption ? (
          <DriMonthToDateConsumption
            planId={planId}
            period={period}
            materials={materials}
          />
        ) : (
          <Box />
        )}

        <Stack spacing={1}>
          <CommentCell onCommentChange={handleOnCommentChange} />
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              disabled={!hasEdited || isSubmitting}
              onClick={handleOnCancel}
              color="primary"
              variant="text"
              sx={{
                whiteSpace: "nowrap",
              }}
              fullWidth
            >
              {t("discardChanges")}
            </Button>
            <LoadingButton
              loading={isSubmitting}
              disabled={!hasEdited}
              onClick={handleOnSubmit}
              color="primary"
              variant="contained"
              sx={{ whiteSpace: "nowrap" }}
              fullWidth
            >
              {t("saveChanges")}
            </LoadingButton>
          </Box>
        </Stack>
      </Stack>
      <Stack
        sx={{
          flex: 1,
          width: "100%",
          overflow: "auto",
        }}
      >
        {children(
          handleOnTableChange,
          setMixDescription,
          isSubmitting,
          hasEdited,
          editedRows
        )}
      </Stack>
    </Stack>
  );
};
